import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { PostStaffRotationBody, PutStaffRotationBody, RegionId } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/** Fetches a region's rotas, past and future, ordered by start date. */
export function useListStaffRotations(region: RegionId) {
  const api = useOsApi()
  return useQuery({
    queryKey: ['staff-rotations', region],
    queryFn: async () => {
      const response = await api.get('/schedules/rotations', { query: { region } })
      if (!response.success) throw new Error(response.error.message)
      return response.data.rotations
    },
  })
}

function invalidateRota(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['staff-rotations'] })
  queryClient.invalidateQueries({ queryKey: ['clinic-schedule'] })
  // The rota feeds booking availability, so bust the slot queries too — otherwise
  // the calendar keeps offering stale bookable/unbookable slots until a reload.
  queryClient.invalidateQueries({ queryKey: ['available-slots-range'] })
  queryClient.invalidateQueries({ queryKey: ['available-slots'] })
}

/** Creates a rota for a vet; resolves with the new rota. */
export function useCreateStaffRotation() {
  const api = useOsApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: PostStaffRotationBody) => {
      const response = await api.post('/schedules/rotations', { body })
      if (!response.success) throw new Error(response.error.message)
      return response.data.rotation
    },
    onSuccess: () => invalidateRota(queryClient),
  })
}

/** Replaces a rota's dates and grid. */
export function useUpdateStaffRotation() {
  const api = useOsApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PutStaffRotationBody }) => {
      const response = await api.put('/schedules/rotations/:id', { params: { id }, body })
      if (!response.success) throw new Error(response.error.message)
    },
    onSuccess: () => invalidateRota(queryClient),
  })
}

/** Deletes a rota. */
export function useDeleteStaffRotation() {
  const api = useOsApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete('/schedules/rotations/:id', { params: { id } })
      if (!response.success) throw new Error(response.error.message)
    },
    onSuccess: () => invalidateRota(queryClient),
  })
}
